import React, { useEffect, useMemo, useState } from "react";

const Frontend = () => {
  const [isOpen, setIsOpen] = useState(false);

  const skillsCatalog = useMemo(
    () => [
      {
        name: "HTML5 & Semantic Markup",
        level: "Expert",
        icon: "bxl-html5",
        desc: "Accessible, SEO-friendly structure using semantic tags and best practices.",
      },
      {
        name: "CSS3 / Responsive Design",
        level: "Expert",
        icon: "bxl-css3",
        desc: "Modern layouts (Flex/Grid), responsive UI, and cross-device consistency.",
      },
      {
        name: "JavaScript (ES6+)",
        level: "Advanced",
        icon: "bxl-javascript",
        desc: "Modern JS patterns, async flows, DOM logic, and frontend architecture.",
      },
      {
        name: "TypeScript",
        level: "Advanced",
        icon: "bxl-typescript",
        desc: "Type-safe components, clean contracts, scalability, fewer runtime bugs.",
      },
      {
        name: "React.js",
        level: "Advanced",
        icon: "bxl-react",
        desc: "Component-driven UI, hooks, state patterns, reusable systems, performance.",
      },
      {
        name: "Next.js",
        level: "Advanced",
        icon: "bx-layer",
        desc: "SSR/SSG, routing, SEO, app architecture, and production-ready frontend apps.",
      },
      {
        name: "Tailwind CSS",
        level: "Advanced",
        icon: "bxl-tailwind-css",
        desc: "Rapid UI building with consistent design tokens and scalable utilities.",
      },
      {
        name: "State Management (Hooks/Context)",
        level: "Advanced",
        icon: "bx-refresh",
        desc: "Predictable app state using React patterns, memoization, and clean flows.",
      },
      {
        name: "REST APIs & Integration",
        level: "Advanced",
        icon: "bx-network-chart",
        desc: "API integration, error handling, loading states, data mapping, caching strategies.",
      },
      {
        name: "Git & GitHub Workflow",
        level: "Advanced",
        icon: "bxl-git",
        desc: "Branching, PR discipline, conflict resolution, code reviews, releases.",
      },
      {
        name: "Performance Optimization",
        level: "Intermediate",
        icon: "bx-tachometer",
        desc: "Core Web Vitals, lazy loading, memoization, bundle hygiene, faster UIs.",
      },
      {
        name: "Accessibility (WCAG)",
        level: "Intermediate",
        icon: "bx-accessibility",
        desc: "Keyboard navigation, contrast, ARIA labels, inclusive UX standards.",
      },
      {
        name: "Component Architecture",
        level: "Advanced",
        icon: "bx-code-alt",
        desc: "Reusable component systems, clean props contracts, scalable UI patterns.",
      },
      {
        name: "Debugging & Testing",
        level: "Advanced",
        icon: "bx-test-tube",
        desc: "Debugging workflows, frontend testing mindset, stability and correctness.",
      },
      {
        name: "Figma-to-Code (Pixel Perfect)",
        level: "Advanced",
        icon: "bx-brush",
        desc: "Accurate implementation of design systems, spacing, typography, and layout details.",
      },
      {
        name: "Mobile-First & Cross-Browser UI",
        level: "Advanced",
        icon: "bx-mobile-alt",
        desc: "Build interfaces that work cleanly across devices and modern browsers.",
      },
    ],
    []
  );

  const topSkills = useMemo(() => skillsCatalog.slice(0, 5), [skillsCatalog]);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  // ESC to close + lock body scroll
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  return (
    <div className="skillsContent">
      <h3 className="skillsTitle">Frontend Engineer</h3>

      <div className="skillsBox">
        <div className="skillsGroup">
          {topSkills.map((skill) => (
            <div className="skillsData" key={skill.name}>
              <i className={`bx ${skill.icon}`}></i>
              <div>
                <h3 className="skillsName">{skill.name}</h3>
                <span className="skillsLevel">{skill.level}</span>
              </div>
            </div>
          ))}

          <button className="skillsMoreBtn" type="button" onClick={openModal}>
            <span className="skillsMoreBtn__pulse" aria-hidden="true"></span>
            I can do more — <strong>View full catalog</strong>
            <i className="bx bx-right-arrow-alt"></i>
          </button>
        </div>
      </div>

      {/* MODAL */}
      {isOpen && (
        <div className="skillsModalOverlay" role="dialog" aria-modal="true">
          <div className="skillsModalBackdrop" onClick={closeModal} />

          <div className="skillsModal">
            <div className="skillsModalHeader">
              <div>
                <h3 className="skillsModalTitle">Frontend Skill Catalog</h3>
                <p className="skillsModalSubtitle">
                  Tools and practices I use to build professional, scalable, pixel-perfect interfaces.
                </p>
              </div>

              <button
                className="skillsModalClose"
                type="button"
                onClick={closeModal}
                aria-label="Close"
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="skillsCatalog">
              {skillsCatalog.map((skill, idx) => (
                <div
                  className="skillsCard"
                  key={skill.name}
                  style={{ animationDelay: `${idx * 25}ms` }}
                >
                  <div className="skillsCardTop">
                    <i className={`bx ${skill.icon}`}></i>
                    <div className="skillsCardMeta">
                      <h4 className="skillsCardTitle">{skill.name}</h4>
                      <span className="skillsCardLevel">{skill.level}</span>
                    </div>
                  </div>
                  <p className="skillsCardDesc">{skill.desc}</p>
                </div>
              ))}
            </div>

            <div className="skillsModalFooter">
              <span className="skillsHint">
                Tip: press <kbd>ESC</kbd> to close
              </span>
              <button className="skillsModalPrimary" type="button" onClick={closeModal}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Frontend;
